// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewPinViewControls.test.tsx
//
// All pins or groups, what to group by, and whether minor and check findings
// show (warhol spec dave.savostyanov/0090). Presentational: the state is
// `useAskV2ReviewPinView`'s.
import { CaretDown } from "@phosphor-icons/react";
import { Checkbox, Popover, RadioGroup, Text } from "@radix-ui/themes";
import { useTranslation } from "react-i18next";

import { type PinGroupBy } from "./pinGroups";
import { type PinViewMode } from "./useAskV2ReviewPinView";

type AskV2ReviewPinViewControlsProps = {
  mode: PinViewMode;
  groupBy: PinGroupBy;
  showMinor: boolean;
  pinCount: number;
  groupCount: number;
  onModeChange: (mode: PinViewMode) => void;
  onGroupByChange: (groupBy: PinGroupBy) => void;
  onShowMinorChange: (showMinor: boolean) => void;
};

const SEGMENT =
  "flex items-center gap-1 rounded-md px-2 py-1 text-[12px] font-medium transition-colors";

export function AskV2ReviewPinViewControls({
  mode,
  groupBy,
  showMinor,
  pinCount,
  groupCount,
  onModeChange,
  onGroupByChange,
  onShowMinorChange,
}: AskV2ReviewPinViewControlsProps) {
  const { t } = useTranslation("ui");
  const segment = (value: PinViewMode, label: string, count: number) => (
    <button
      type="button"
      role="radio"
      aria-checked={mode === value}
      className={`${SEGMENT} ${
        mode === value
          ? "bg-base-white text-puntt-neutral-gray-12 shadow-sm"
          : "text-puntt-neutral-gray-10 hover:text-puntt-neutral-gray-12"
      }`}
      data-testid={`ask-v2-pin-view-mode-${value}`}
      onClick={() => onModeChange(value)}
    >
      {label}
      <span className="tabular-nums text-puntt-neutral-gray-10">{count}</span>
    </button>
  );

  return (
    <div className="flex items-center gap-2">
      <div
        role="radiogroup"
        aria-label={t("askV2.review.pinView.label")}
        className="flex rounded-lg bg-puntt-neutral-gray-3 p-0.5"
        data-testid="ask-v2-pin-view-mode"
      >
        {segment("flat", t("askV2.review.pinView.flat"), pinCount)}
        {segment("groups", t("askV2.review.pinView.groups"), groupCount)}
      </div>
      <Popover.Root>
        <Popover.Trigger>
          <button
            type="button"
            className="flex items-center gap-1 rounded-md border border-puntt-neutral-gray-5 bg-base-white px-2 py-1 text-[12px] font-medium text-puntt-neutral-gray-11 hover:bg-puntt-neutral-gray-2"
            data-testid="ask-v2-pin-view-menu-trigger"
          >
            {t("askV2.review.pinView.menu")}
            <CaretDown size={12} aria-hidden="true" />
          </button>
        </Popover.Trigger>
        <Popover.Content
          className="max-h-[min(70vh,420px)] w-64 overflow-y-auto p-3"
          data-testid="ask-v2-pin-view-menu"
          aria-label={t("askV2.review.pinView.menuLabel")}
        >
          <Text
            as="p"
            size="1"
            weight="medium"
            className="mb-2 text-puntt-neutral-gray-10"
          >
            {t("askV2.review.pinView.groupBy")}
          </Text>
          <RadioGroup.Root
            size="1"
            value={groupBy}
            onValueChange={(value) => onGroupByChange(value as PinGroupBy)}
            aria-label={t("askV2.review.pinView.groupBy")}
          >
            <Text as="label" size="2" className="flex items-center gap-2">
              <RadioGroup.Item
                value="block"
                data-testid="ask-v2-pin-group-by-block"
              />
              {t("askV2.review.pinView.byBlock")}
            </Text>
            <Text as="label" size="2" className="flex items-center gap-2">
              <RadioGroup.Item
                value="place"
                data-testid="ask-v2-pin-group-by-place"
              />
              {t("askV2.review.pinView.byPlace")}
            </Text>
          </RadioGroup.Root>
          <Text
            as="label"
            size="2"
            className="mt-3 flex cursor-pointer items-center gap-2 border-t border-puntt-neutral-gray-4 pt-3"
          >
            <Checkbox
              checked={showMinor}
              onCheckedChange={(value) => onShowMinorChange(value === true)}
              data-testid="ask-v2-pin-show-minor"
            />
            {t("askV2.review.pinView.showMinor")}
          </Text>
        </Popover.Content>
      </Popover.Root>
    </div>
  );
}
